import type {
  FiboCollectionComboboxItem,
  FiboHeadline,
  FiboSuggestionComboboxItem,
  FiboSuggestionItem,
  GroupedFiboHeadlineWithItems,
  GroupedFiboSuggestionItemList,
} from '@/models/fibo/fibosearch';
import { FiboSearchHeadline, FiboSearchType } from '@/constants/fibo/fibosearch';
import type { CollectionItem } from '@chakra-ui/react';

function isFiboHeadline(item: FiboSuggestionComboboxItem): item is FiboHeadline {
  return item.type === FiboSearchType.HEADLINE;
}

function isFiboProductOrTaxonomy(item: FiboSuggestionComboboxItem): item is FiboCollectionComboboxItem {
  return item.type === FiboSearchType.PRODUCT || item.type === FiboSearchType.TAXONOMY;
}

export function groupFiboSuggestionsToTuples(suggestions: FiboSuggestionComboboxItem[]): GroupedFiboSuggestionItemList {
  const groupedObj: Partial<Record<FiboSearchHeadline, GroupedFiboHeadlineWithItems>> = {};
  let currentHeadline: FiboHeadline | null = null;

  for (const item of suggestions) {
    if (isFiboHeadline(item)) {
      currentHeadline = item;
      if (Object.values(FiboSearchHeadline).includes(currentHeadline.value as FiboSearchHeadline)) {
        groupedObj[currentHeadline.value] = {
          ...currentHeadline,
          value: currentHeadline.value as FiboSearchHeadline,
          items: [],
        };
      }
    } else if (currentHeadline && isFiboProductOrTaxonomy(item)) {
      const headlineKey = currentHeadline.value as FiboSearchHeadline;
      groupedObj[headlineKey]?.items.push(item);
    }
  }

  return Object.entries(groupedObj) as GroupedFiboSuggestionItemList;
}

/**
 * Function to extract image path from HTML image tag
 * @param imgTag - HTML image tag string (e.g., <img src="path">)
 * @returns Image path string, or empty string if extraction fails
 */
export const extractImageSrc = (imgTag: string): string => {
  const srcRegex = /src=["']([^"']+)["']/i;
  const match = imgTag?.match(srcRegex);
  return match && match[1] ? match[1] : '';
};

export const convertToCollectionItem = (item: FiboSuggestionItem): CollectionItem => {
  if (item.type === FiboSearchType.TAXONOMY) {
    return {
      ...item,
      value: item.termId,
      label: item.value,
    };
  }
  if (item.type === FiboSearchType.PRODUCT) {
    return {
      ...item,
      value: item.postId,
      label: item.value,
    };
  }
  return item;
};
